
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

import { CalculationType, CaseDetails, DataType, User, Case, CaseField, Permission } from "@/types"
import { calculatedValueDisplay } from "@/mixins/calculatedValueDisplay"

import { cloneDeep, get } from "lodash"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import UserImage from "@/components/UserImage.vue"
import DateTime from "../Fields/DateTime.vue"
import Numeric from "../Fields/Numeric.vue"

import api from "@/services/api"
import moment from "moment"

import vSelect from "vue-select"
import { FormattingRule, Operator, dataTypeTests } from "@/types/insights"
import { CaseColumn } from "@/types/caseColumns"
import { cellFormatting } from "@/mixins/cellFormatting"
import { UserSettings } from "@/types/userSettings"
import TimeSpentDisplay from "../CaseDetails/TimeSpentDisplay.vue"
import TextAreaToggleExpand from "../Fields/TextAreaToggleExpand.vue"
import UserRole from "../CaseDetails/Users/UserRole.vue"
import FormGroup from "../Fields/FormGroup.vue"
import DataSourceSelect from "../Fields/DataSourceSelect.vue"
import { CurrentPageDetails } from "@/types/paginatedRequest"
import IconUpload from "@/components/IconUpload.vue"

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"user-image": UserImage,
		"time-spent-display": TimeSpentDisplay,
		"text-area-expand": TextAreaToggleExpand,
		"date-time": DateTime,
		"numeric": Numeric,
		"user-role": UserRole,
		"form-group": FormGroup,
		"data-select": DataSourceSelect,
		"icon-upload": IconUpload
	}
})
export default class CaseTable extends Vue {
	@Getter("getMe") public me!: User;

	@Getter("getUserSettings")
	public userSettings!: UserSettings;

	@Getter("getTenantSettings") getTenantSettings!: { [ key: string]: any }

	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@caseStore.Action("setFieldLockPolling")
	private setFieldLockPolling!: ({ caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string }) => void;

	@caseStore.Action("cancelFieldLockPolling")
	private cancelFieldLockPolling!: ({ caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string }) => void;

	@Prop()
	columns!: CaseColumn[];

	@Prop({ default: () => { return {} } })
	formattingRules!: { [key: string]: FormattingRule[] }

	@Prop()
	cases!: CaseDetails[];

	@Prop({ default: () => null })
	currentPage!: number | null;

	@Prop({ default: true })
	hover!: boolean;

	@Prop({ default: 20 })
	perPage!: number;

	@Prop({ default: () => [] })
	filterFunctions!: ((record: CaseDetails) => boolean)[];

	@Prop({ default: false })
	public displayingUsers!: boolean;

	@Prop()
	value!: string;

	@Prop({ default: "" })
	sortColumn!: string;

	@Prop({ default: false })
	sortDesc!: boolean;

	@Prop({ type: String, default: null })
	tableId!: string | null;

	@Prop({ type: Number, default: 0 })
	resultsUpdateHook!: number;

	private cellUpdateReactivityHook: number = 0
	sortColumnKey: string | null = null
	sortAscending: boolean = true
	rowHoverEnabled: boolean = this.hover

	private CalculationType = CalculationType;

	get columnValues (): { [key: string]: any }[] {
		const hook = this.cellUpdateReactivityHook

		return this.cases.map(c => {
			return this.columns.reduce((values: { [key: string]: any }, column) => {
				if (c) {
					values[column.key] = this.cellValue(column, c)
				}
				return values
			}, { case: c })
		})
	}

	get sortedColumnValues () {
		return this.sortColumnKey
			? this.columnValues!.sortByValue(this.sortColumnKey, !this.sortAscending)
			: this.columnValues
	}

	get currentPageItems (): { [key: string]: any }[] {
		if (this.tableId !== null) {
			return this.sortedColumnValues
		}

		if (!this.perPage || this.currentPage === null) {
			return this.sortedColumnValues
		}

		return this.sortedColumnValues.slice((this.currentPage - 1) * this.perPage, this.currentPage * this.perPage)
	}

	get undynamicFormattingRules (): { [key: string]: FormattingRule[] } {
		if (!this.formattingRules) {
			return {}
		}

		const cloneRules = cloneDeep(this.formattingRules)
		Object.keys(cloneRules).forEach((key: string) => {
			cloneRules[key].forEach(rule => {
				if (rule.dynamicValue) {
					const column = this.columns.find(c => c.key === key)
					if (column) {
						if (column.fieldDataType === DataType.dateTime) {
							const now = new Date()
							now.setDate(now.getDate() + parseInt(rule.value))
							rule.value = now
						} else if (column.fieldDataType === DataType.userRole) {
							rule.value = this.me.id
						}
					}
				}
			})
		})
		return cloneRules
	}

	@Watch("resultsUpdateHook")
	private onResultsUpdateHook () {
		// Force reactivity when results are updated since the object is readonly.
		this.cellUpdateReactivityHook++
	}

	/**
	 * Get field Label value for field ID
	 */
	public getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields || !caseDetails.fields[fieldId]) {
			return ""
		}

		if (Array.isArray(caseDetails.fields[fieldId])) {
			return caseDetails.fields[fieldId].value.map((i: { label: string }) => i.label).join(", ")
		}

		return caseDetails.fields[fieldId].label
	}

	public rowClick (caseitem: any, ctrlClick: boolean = false) {
		if (!ctrlClick && this.disableRowClick) {
			return
		}

		if (!caseitem) {
			return
		} else if (caseitem.caseNumber === "FORECAST") {
			this.$emit("forecastClicked", caseitem)
			return
		}

		// newTab is default
		var viewsOpening = "newTab"
		if (this.userSettings && this.userSettings.settings && this.userSettings.settings.viewsOpening) {
			viewsOpening = this.userSettings.settings.viewsOpening
		}

		let entityIdentifier = caseitem.caseNumber
		let endPath = "cases"

		if (this.displayingUsers) {
			entityIdentifier = caseitem.id
			endPath = "userprofile"
		}

		const encodedEntityIdentifier = encodeURIComponent(entityIdentifier)
		// Navigate to record in the same tab or in a new tab
		switch (viewsOpening) {
			case "sameTab":
				if (ctrlClick) {
					let routeData = this.$router.resolve({ path: `/${endPath}/${encodedEntityIdentifier}` })
					window.open(routeData.href, "_blank")
				} else {
					this.$router.push({ path: `/${endPath}/${encodedEntityIdentifier}` })
				}
				break
			case "newTab":
				if (ctrlClick) {
					this.$router.push({ path: `/${endPath}/${encodedEntityIdentifier}` })
				} else {
					let routeData = this.$router.resolve({ path: `/${endPath}/${encodedEntityIdentifier}` })
					window.open(routeData.href, "_blank")
				}
				break
		}
	}

	public cellValue (column: CaseColumn, caseItem: CaseDetails): any {
		const value = get(caseItem, column.key)

		if (column.fieldExtensions && column.fieldExtensions.calculationType === CalculationType.DaysUntilOrFromToday) {
			const fieldValue = get(caseItem, "fields." + column.fieldExtensions.dateField)

			if (fieldValue) {
				const fieldValueDate: Date = new Date(fieldValue.value ? fieldValue.value : fieldValue)
				const daysDifference = calculatedValueDisplay.methods.localDaysFromToday(fieldValueDate)

				return { value: daysDifference }
			}
		}

		const label = column.dynamicField
			? (value ? value.label : undefined)
			: column.displayKey
				? get(caseItem, column.displayKey)
				: undefined

		return {
			label: label,
			value: (column.dynamicField ? (value ? value.value : null) : value),
			fieldType: value && value.fieldDataType
		}
	}

	public itemValue (caseItem: any, columnKey: string): any {
		if (!caseItem) {
			return null
		}

		const dotIndex = columnKey.indexOf(".")
		if (dotIndex > -1) {
			const topLevel = columnKey.substring(0, dotIndex)
			const rest = columnKey.substring(dotIndex + 1)
			return this.itemValue(caseItem[topLevel], rest)
		}
		var value = caseItem[columnKey]

		// If the items a calulated field
		if (caseItem.dataType === "calculated" && typeof value === "object") {
			// Add the field's id to the value object
			value.id = caseItem.id
		}

		return value
	}

	public calculatedFieldDisplayValue (field: any, caseValue: any): string | null {
		if (!field) {
			return null
		}

		const value = this.itemValue(caseValue, field.key)
		const fieldExtensions = field.extensions ? field.extensions : field.fieldExtensions

		if (!fieldExtensions || !value) {
			return null
		}

		return calculatedValueDisplay.methods.displayCalculatedField(caseValue, fieldExtensions, value.value)
	}

	private isTimeSpentCalculationType (field: any) : boolean {
		const fieldExtensions = field.extensions ? field.extensions : field.fieldExtensions
		return fieldExtensions && fieldExtensions.calculationType === CalculationType.TimeSpentAs
	}

	private cellStyle (key: string, value: any, field: CaseColumn) {
		if (!this.undynamicFormattingRules || !this.undynamicFormattingRules[key]) return null
		return cellFormatting.cellStyle(this.undynamicFormattingRules[key], value, field)
	}

	private cellBarStyle (caseDetails: { [key: string]: any }, key: string, value: any, field: CaseColumn) {
		if (!this.undynamicFormattingRules || !this.undynamicFormattingRules[key]) {
			return {
				display: "none"
			}
		}

		return cellFormatting.cellBarStyle(this.undynamicFormattingRules[key], this.currentPageItems, caseDetails, value, field)
	}

	private icon (key: string, value: any, field: CaseColumn) {
		if (!this.undynamicFormattingRules || !this.undynamicFormattingRules[key]) return null
		return cellFormatting.icon(this.undynamicFormattingRules[key], value, field)
	}

	private setSortColumn (columnKey: string, columnDataType: DataType | undefined): void {
		if (this.tableId !== null) {
			const pageDetails: CurrentPageDetails = {
				sortBy: columnKey,
				sortDesc: this.sortColumn === columnKey ? !this.sortDesc : false,
				sortDataType: columnDataType,
				value: this.value,
				tableId: this.tableId
			}

			this.$emit("sortDetails", pageDetails)
		} else {
			if (this.sortColumnKey === columnKey) {
				this.sortAscending = !this.sortAscending
			} else {
				this.sortColumnKey = columnKey
				this.sortAscending = true
			}
		}
	}

	// Edit Table Cell functionality

	disableRowClick: boolean = false

	columnKeyHovered: string = "";
	caseIdHovered: string = ""
	columnKeyToEdit: string = "";
	caseIdToEdit: string = ""

	caseToEdit: CaseDetails | null = null
	fieldToEdit: CaseField | null = null

	public get fieldEditingEnabled () : boolean {
		if (this.getTenantSettings &&
			"allowFieldEditInViews" in this.getTenantSettings &&
			!!this.getTenantSettings["allowFieldEditInViews"]) {
			return true
		}
		return false
	}

	private cellIsEditable (caseHovered: CaseDetails, column: CaseColumn) : boolean {
		if (!this.pageAllowsEdits) {
			return false
		}

		if (!this.fieldEditingEnabled) {
			return false
		}

		if (caseHovered.isClosed) {
			return false
		}

		// Prevents editing of Forecasted Records
		if (!caseHovered.id) {
			return false
		}

		if (!column.dynamicField) {
			return false
		}

		const dynamicFieldId = this.stripFieldsPrefix(column.key)

		if (caseHovered.fields === null || !(dynamicFieldId in caseHovered.fields)) {
			return false
		}
		const fieldHovered = caseHovered.fields[dynamicFieldId]

		const isEditableType: boolean =
			!!column.fieldId &&
			caseHovered.fields &&
			caseHovered.fields[column.fieldId || ""] &&
			(column.dataType === "text" ||
			column.dataType === "text-area" ||
			column.dataType === "checkbox" ||
			column.dataType === "number" ||
			column.dataType === "dollar" ||
			column.dataType === "date" ||
			column.dataType === "date-time" ||
			column.dataType === "user-role" ||
			column.dataType === "list" ||
			column.dataType === "list-multiple")

		if (!isEditableType) {
			return false
		}

		const hasPermission = this.hasPermission(caseHovered, fieldHovered)
		return hasPermission
	}

	// This checks for field level and case field level permissions and hides pencil icon if not authorized - it's not possible to do anything with *section permissions* for Views as these don't get returned by API (in this case).
	// Even if an edit is made by a user without permission, the API also checks permissions at api.selectCaseField(..), and disallows it with a NoPermissionException if not authorized
	private hasPermission (caseHovered: CaseDetails, fieldHovered: CaseField): boolean {
		if (fieldHovered.field && fieldHovered.field.displayReadOnly) {
			return false
		}

		const teams = this.getPermissionTeams("edit", caseHovered, fieldHovered)
		if (!teams || teams.length === 0) {
			return true
		}

		return this.me.teams && teams.some(team => this.me.teams.find((myTeam: any) => myTeam.id === team.id))
	}

	private getPermissionTeams (permissionType: keyof Permission, caseHovered: CaseDetails, fieldHovered: CaseField): any[] {
		if (!caseHovered || !fieldHovered) {
			return []
		}

		var caseFieldLevelPermissions = fieldHovered.permissions
			? caseHovered.isCase ? fieldHovered.permissions.case : fieldHovered.permissions.record
			: null

		if (caseFieldLevelPermissions) {
			return caseFieldLevelPermissions[permissionType]
		}

		if (fieldHovered.field && fieldHovered.field.permissions) {
			const fieldLevelPermissions =
				(caseHovered.isCase ? fieldHovered.field.permissions.case : fieldHovered.field.permissions.record)[permissionType]
			return fieldLevelPermissions
		}

		return []
	}

	private async cellEditBegin (caseNumber: string) {
		this.caseIdToEdit = this.caseIdHovered
		this.columnKeyToEdit = this.columnKeyHovered
		const fieldToEditId = this.stripFieldsPrefix(this.columnKeyToEdit)

		await api.selectCaseField(this.caseIdToEdit, fieldToEditId, true)

		this.caseToEdit = await api.caseDetails(caseNumber)
		this.setFieldLockPolling({ caseDetails: this.caseToEdit, fieldId: fieldToEditId })

		this.fieldToEdit = this.caseToEdit.fields[fieldToEditId]
		this.disableRowClick = true
		this.rowHoverEnabled = false
	}

	private stripFieldsPrefix (fieldId: string) : string {
		if (fieldId.startsWith("fields.")) {
			return fieldId.slice("fields.".length)
		}
		return fieldId
	}

	private async cellEditClear () {
		await api.selectCaseField(this.caseIdToEdit, this.fieldToEdit!.field.id!, false)
		if (this.caseToEdit) {
			this.setFieldLockPolling({ caseDetails: this.caseToEdit, fieldId: this.fieldToEdit!.field.id! })
		}
		this.caseIdToEdit = ""
		this.columnKeyToEdit = ""
		this.caseToEdit = null
		this.fieldToEdit = null
		this.disableRowClick = false
		this.rowHoverEnabled = true
	}

	private async cellEditConfirmed (caseNumber: string) {
		await api.saveCaseField(this.caseToEdit!.id!, this.fieldToEdit!.field.id!, this.fieldToEdit!.value)
		const updatedCase = await api.caseDetails(caseNumber)
		const caseIndex = this.cases.findIndex(c => c.caseNumber === caseNumber)
		this.$set(this.cases, caseIndex, updatedCase)
		await this.cellEditClear()
		this.cellUpdateReactivityHook++ // Force reactivity for inline edit.

		this.$emit("caseEdited", caseNumber)
	}

	private cellIsHovered (caseIdHovered: string, columnKeyHovered: string) : boolean {
		return caseIdHovered === this.caseIdHovered && columnKeyHovered === this.columnKeyHovered
	}

	private setHoveringCell (caseIdHovered: string, columnKeyHovered: string) {
		this.columnKeyHovered = columnKeyHovered
		this.caseIdHovered = caseIdHovered
	}

	private clearHoveringCell () {
		this.columnKeyHovered = ""
		this.caseIdHovered = ""
	}

	private cellIsInEditMode (caseIdToEdit: string, columnKeyToEdit: string) : boolean {
		if (!this.fieldEditingEnabled) {
			return false
		}

		return this.caseIdToEdit === caseIdToEdit &&
		this.columnKeyToEdit === columnKeyToEdit &&
		!!this.fieldToEdit
	}

	public get pageAllowsEdits () {
		return this.$route.name === "dashboard" ||
			this.$route.name === "overview" || // views
			this.$route.name === "insights"
	}

	private get currentSortColumn (): string | null {
		return this.tableId !== null ? this.sortColumn : this.sortColumnKey
	}

	private get currentSortAscending (): boolean {
		return this.tableId !== null ? !this.sortDesc : this.sortAscending
	}
}

